import { useState, useEffect, useCallback } from 'react'
import { appsyncQuery } from '../../api/appsync-client.js'
import { useSubscription } from '../../api/useSubscription.js'
import { parseTimeHorizon } from '../timeHorizon.js'

function parseGoal(goal) {
  if (!goal) return goal
  const parsed = { ...goal, timeHorizon: parseTimeHorizon(goal.timeHorizon) }
  if (parsed.subgoals) {
    parsed.subgoals = parsed.subgoals.map((sg) => ({
      ...sg,
      timeHorizon: parseTimeHorizon(sg.timeHorizon),
    }))
  }
  return parsed
}

const GOAL_FIELDS = `
  goalId ownerUserId managerId createdBy
  title description goalType timeHorizon
  status statusLabel
  createdAt updatedAt achievedAt droppedAt droppedReason
  rollup { total notStarted onTrack atRisk blocked done }
  subgoals { subgoalId goalId title primaryFacet facets status notes timeHorizon tracking createdAt updatedAt }
  comments { entryId goalId createdBy createdByName text createdAt }
`

const GET_GOAL = `
  query GetGoal($goalId: ID!, $ownerUserId: ID) {
    getGoal(goalId: $goalId, ownerUserId: $ownerUserId) {
      ${GOAL_FIELDS}
    }
  }
`

const ON_GOAL_UPDATED = `
  subscription OnGoalUpdated($goalId: ID!) {
    onGoalUpdated(goalId: $goalId) {
      ${GOAL_FIELDS}
    }
  }
`

const ON_SUBGOAL_UPDATED = `
  subscription OnSubgoalUpdated($goalId: ID!) {
    onSubgoalUpdated(goalId: $goalId) { subgoalId }
  }
`

const ON_COMMENT_ADDED = `
  subscription OnCommentAdded($goalId: ID!) {
    onCommentAdded(goalId: $goalId) { entryId }
  }
`

export function useGoal({ goalId, ownerUserId = null, enabled = true } = {}) {
  const [goal, setGoal] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!enabled || !goalId) return
    setLoading(true)
    setError(null)
    try {
      const data = await appsyncQuery(GET_GOAL, { goalId, ownerUserId })
      setGoal(parseGoal(data.getGoal))
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [goalId, ownerUserId, enabled])

  useEffect(() => { load() }, [load])

  useSubscription(
    ON_GOAL_UPDATED,
    { goalId },
    (data) => {
      const updated = data.onGoalUpdated
      if (updated) setGoal(updated)
    },
    { enabled: enabled && !!goalId },
  )

  // Reload the full goal when sub-goals or comments change
  useSubscription(ON_SUBGOAL_UPDATED, { goalId }, () => load(), { enabled: enabled && !!goalId })
  useSubscription(ON_COMMENT_ADDED, { goalId }, () => load(), { enabled: enabled && !!goalId })

  return { goal, loading, error, reload: load }
}
